import './SemanticDataTable.css';
import * as React from 'react';
import { DropdownItemProps, Pagination, PaginationProps, Table } from 'semantic-ui-react';
import { SemanticSelectTableFilter, SemanticTextTableFilter } from './SemanticTableFilters';

type SortDirection = 'ascending' | 'descending';
type MatchMode = 'contains' | 'equals' | 'startsWith';
type FilterValue = string | { values: string[]; displayValue?: string };
type PrimitiveValue = number | string | null | undefined;
type SemanticTableWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16;

interface BaseColumn<T> {
    id: string;
    title: React.ReactNode;
    render: (row: T, rowIndex: number) => React.ReactNode;
    width?: SemanticTableWidth;
    headerStyle?: React.CSSProperties;
    cellStyle?: React.CSSProperties;
    textAlign?: 'left' | 'center' | 'right';
    sortable?: boolean;
    getSortValue?: (row: T) => PrimitiveValue;
    getFilterValue?: (row: T) => PrimitiveValue;
}

interface TextFilterConfig {
    type: 'text';
    placeholder?: string;
    debounceMs?: number;
    matchMode?: MatchMode;
    normalize?: (value: string) => string;
}

interface SelectFilterConfig {
    type: 'select';
    placeholder?: string;
    options: DropdownItemProps[];
}

export type SemanticDataTableColumn<T> = BaseColumn<T> & {
    filter?: TextFilterConfig | SelectFilterConfig;
};

export interface SemanticDataTableHandle {
    setFilterValue: (columnId: string, value: string) => void;
    setFilterValues: (columnId: string, values: string[], displayValue?: string) => void;
    clearFilters: () => void;
}

interface SemanticDataTableProps<T> {
    columns: SemanticDataTableColumn<T>[];
    data: T[];
    rowKey?: (row: T, rowIndex: number) => React.Key;
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    height?: number | string;
    minHeight?: number | string;
    maxHeight?: number | string;
    pageSize?: number;
    className?: string;
    emptyMessage?: string;
    defaultSortColumnId?: string;
    defaultSortDirection?: SortDirection;
    singleLine?: boolean;
    compact?: boolean;
    fixed?: boolean;
    onRowClick?: (row: T) => void;
    isRowActive?: (row: T) => boolean;
}

interface SortState {
    columnId: string;
    direction: SortDirection;
}

interface TableRowProps<T> {
    columns: SemanticDataTableColumn<T>[];
    row: T;
    rowIndex: number;
    rowKey: React.Key;
    active: boolean;
    onRowClick?: (row: T) => void;
}

interface PaginatedRow<T> {
    row: T;
    rowIndex: number;
}

const defaultNormalize = (value: string) => value.trim().toLowerCase();

const stringifyValue = (value: PrimitiveValue): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
};

const compareValues = (firstValue: PrimitiveValue, secondValue: PrimitiveValue): number => {
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return firstValue - secondValue;
    }

    return stringifyValue(firstValue).localeCompare(stringifyValue(secondValue), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
};

const getDisplayValue = (value: FilterValue | undefined): string => {
    if (typeof value === 'string' || value === undefined) {
        return value ?? '';
    }

    return value.displayValue ?? (value.values.length === 1 ? value.values[0] : '');
};

function getColumnLayoutStyle<T>(column: SemanticDataTableColumn<T>): React.CSSProperties | undefined {
    const width = column.cellStyle?.width ?? column.headerStyle?.width;
    const minWidth = column.cellStyle?.minWidth ?? column.headerStyle?.minWidth;
    const maxWidth = column.cellStyle?.maxWidth ?? column.headerStyle?.maxWidth;

    if (width === undefined && minWidth === undefined && maxWidth === undefined) {
        return undefined;
    }

    return {
        width,
        minWidth,
        maxWidth,
    };
}

function TableRowInner<T>({ columns, row, rowIndex, rowKey, active, onRowClick }: TableRowProps<T>) {
    return (
        <Table.Row
            key={rowKey}
            active={active}
            onClick={onRowClick ? () => onRowClick(row) : undefined}>
            {columns.map((column) => (
                <Table.Cell key={column.id} textAlign={column.textAlign} style={column.cellStyle}>
                    {column.render(row, rowIndex)}
                </Table.Cell>
            ))}
        </Table.Row>
    );
}

function tableRowPropsAreEqual<T>(previousProps: TableRowProps<T>, nextProps: TableRowProps<T>) {
    return previousProps.row === nextProps.row
        && previousProps.rowIndex === nextProps.rowIndex
        && previousProps.columns === nextProps.columns
        && previousProps.active === nextProps.active
        && previousProps.onRowClick === nextProps.onRowClick
        && previousProps.rowKey === nextProps.rowKey;
}

const MemoTableRow = React.memo(TableRowInner, tableRowPropsAreEqual) as <T>(props: TableRowProps<T>) => React.ReactElement;

function SemanticDataTableInner<T>(
    {
        columns,
        data,
        rowKey,
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        pageSize = 10,
        className,
        emptyMessage = 'No data found.',
        defaultSortColumnId,
        defaultSortDirection = 'ascending',
        singleLine = true,
        compact = true,
        fixed = true,
        onRowClick,
        isRowActive,
    }: SemanticDataTableProps<T>,
    ref: React.Ref<SemanticDataTableHandle>,
) {
    const [sortState, setSortState] = React.useState<SortState | undefined>(
        defaultSortColumnId ? { columnId: defaultSortColumnId, direction: defaultSortDirection } : undefined,
    );
    const [filtersState, setFiltersState] = React.useState<Record<string, FilterValue>>({});
    const [activePage, setActivePage] = React.useState(1);

    const hasSortableColumns = React.useMemo(
        () => columns.some((column) => column.sortable !== false),
        [columns],
    );

    const hasFilterableColumns = React.useMemo(
        () => columns.some((column) => column.filter !== undefined),
        [columns],
    );

    const columnsById = React.useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);

    const setFilterStateValue = React.useCallback((columnId: string, value: FilterValue | undefined) => {
        setFiltersState((previousState) => {
            if (value === undefined || (typeof value === 'string' && value.length === 0)) {
                if (!(columnId in previousState)) {
                    return previousState;
                }

                const nextState = { ...previousState };
                delete nextState[columnId];
                return nextState;
            }

            if (typeof value !== 'string' && value.values.length === 0) {
                if (!(columnId in previousState)) {
                    return previousState;
                }

                const nextState = { ...previousState };
                delete nextState[columnId];
                return nextState;
            }

            return {
                ...previousState,
                [columnId]: value,
            };
        });
    }, []);

    React.useImperativeHandle(ref, () => ({
        setFilterValue(columnId: string, value: string) {
            const column = columnsById.get(columnId);
            if (!column) {
                return;
            }

            setFilterStateValue(columnId, value);
        },
        setFilterValues(columnId: string, values: string[], displayValue?: string) {
            const column = columnsById.get(columnId);
            if (!column) {
                return;
            }

            setFilterStateValue(columnId, { values, displayValue });
        },
        clearFilters() {
            setFiltersState({});
        },
    }), [columnsById, setFilterStateValue]);

    const handleSort = React.useCallback((columnId: string, sortable?: boolean) => {
        if (sortable === false) {
            return;
        }

        setSortState((previousState) => {
            if (previousState?.columnId === columnId) {
                return {
                    columnId,
                    direction: previousState.direction === 'ascending' ? 'descending' : 'ascending',
                };
            }

            return {
                columnId,
                direction: 'ascending',
            };
        });
    }, []);

    const safePageSize = Math.max(1, pageSize);

    const filteredAndSortedData = React.useMemo(() => {
        const filtered = data.filter((row) => {
            return Object.entries(filtersState).every(([columnId, filterValue]) => {
                const column = columnsById.get(columnId);
                if (!column) {
                    return true;
                }

                const rawValue = stringifyValue((column.getFilterValue ?? column.getSortValue)?.(row));
                const normalizer = column.filter?.type === 'text' ? (column.filter.normalize ?? defaultNormalize) : defaultNormalize;
                const normalizedValue = normalizer(rawValue);

                if (typeof filterValue !== 'string') {
                    const values = filterValue.values.map((value) => normalizer(value));
                    return values.includes(normalizedValue);
                }

                const normalizedFilterValue = normalizer(filterValue);
                const matchMode = column.filter?.type === 'text' ? (column.filter.matchMode ?? 'contains') : 'equals';

                if (normalizedFilterValue.length === 0) {
                    return true;
                }

                switch (matchMode) {
                    case 'startsWith':
                        return normalizedValue.startsWith(normalizedFilterValue);
                    case 'equals':
                        return normalizedValue === normalizedFilterValue;
                    default:
                        return normalizedValue.includes(normalizedFilterValue);
                }
            });
        });

        if (!sortState) {
            return filtered;
        }

        const sortColumn = columnsById.get(sortState.columnId);
        if (!sortColumn) {
            return filtered;
        }

        const sortedData = [...filtered].sort((firstRow, secondRow) => {
            const firstValue = (sortColumn.getSortValue ?? sortColumn.getFilterValue)?.(firstRow);
            const secondValue = (sortColumn.getSortValue ?? sortColumn.getFilterValue)?.(secondRow);
            const result = compareValues(firstValue, secondValue);
            return sortState.direction === 'ascending' ? result : -result;
        });

        return sortedData;
    }, [columnsById, data, filtersState, sortState]);

    React.useEffect(() => {
        setActivePage(1);
    }, [filtersState, sortState]);

    const totalPages = React.useMemo(
        () => Math.max(1, Math.ceil(filteredAndSortedData.length / safePageSize)),
        [filteredAndSortedData.length, safePageSize],
    );

    React.useEffect(() => {
        setActivePage((previousState) => Math.min(previousState, totalPages));
    }, [totalPages]);

    const handlePageChange = React.useCallback((_event: React.MouseEvent<HTMLAnchorElement>, data: PaginationProps) => {
        setActivePage(Math.max(1, Number(data.activePage ?? 1)));
    }, []);

    const paginatedRows = React.useMemo<PaginatedRow<T>[]>(() => {
        const startIndex = (activePage - 1) * safePageSize;
        return filteredAndSortedData
            .slice(startIndex, startIndex + safePageSize)
            .map((row, rowIndex) => ({
                row,
                rowIndex: startIndex + rowIndex,
            }));
    }, [activePage, filteredAndSortedData, safePageSize]);

    const visibleRowsStart = filteredAndSortedData.length === 0
        ? 0
        : ((activePage - 1) * safePageSize) + 1;
    const visibleRowsEnd = filteredAndSortedData.length === 0
        ? 0
        : Math.min(activePage * safePageSize, filteredAndSortedData.length);

    const containerStyle = React.useMemo<React.CSSProperties | undefined>(() => {
        const style: React.CSSProperties = {};

        if (width !== undefined) {
            style.width = width;
        } else if (maxWidth !== undefined) {
            style.maxWidth = maxWidth;
        } else if (minWidth !== undefined) {
            style.minWidth = minWidth;
        }

        if (height !== undefined) {
            style.height = height;
        } else if (maxHeight !== undefined) {
            style.maxHeight = maxHeight;
        } else if (minHeight !== undefined) {
            style.minHeight = minHeight;
        }

        return Object.keys(style).length > 0 ? style : undefined;
    }, [height, maxHeight, minHeight, maxWidth, minWidth, width]);

    return (
        <div className="semanticDataTableRoot">
            <div
                className="semanticDataTableContainer"
                style={containerStyle}>
                <Table
                    celled
                    striped
                    compact={compact}
                    singleLine={singleLine}
                    fixed={fixed}
                    sortable={hasSortableColumns}
                    className={className}>
                    <colgroup>
                        {columns.map((column) => (
                            <col key={column.id} style={getColumnLayoutStyle(column)} />
                        ))}
                    </colgroup>
                    <Table.Header>
                        <Table.Row>
                            {columns.map((column) => {
                                const filterValue = filtersState[column.id];
                                const displayValue = getDisplayValue(filterValue);

                                return (
                                    <Table.HeaderCell
                                        key={column.id}
                                        textAlign={column.textAlign}
                                        style={column.headerStyle}
                                        width={column.width}
                                        sorted={sortState?.columnId === column.id ? sortState.direction : undefined}
                                        onClick={() => handleSort(column.id, column.sortable)}>
                                        <div className="semanticTableHeaderCell">
                                            <div className="semanticTableHeaderLabel">{column.title}</div>
                                            {column.filter?.type === 'text' && (
                                                <SemanticTextTableFilter
                                                    value={displayValue}
                                                    placeholder={column.filter.placeholder}
                                                    debounceMs={column.filter.debounceMs}
                                                    onChange={(value) => setFilterStateValue(column.id, value)}
                                                />
                                            )}
                                            {column.filter?.type === 'select' && (
                                                <SemanticSelectTableFilter
                                                    value={displayValue}
                                                    options={column.filter.options}
                                                    placeholder={column.filter.placeholder}
                                                    onChange={(value) => setFilterStateValue(column.id, value)}
                                                />
                                            )}
                                        </div>
                                    </Table.HeaderCell>
                                );
                            })}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {filteredAndSortedData.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={columns.length} className="semanticTableEmptyCell">
                                    {hasFilterableColumns ? emptyMessage : 'No data found.'}
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {paginatedRows.map(({ row, rowIndex }) => (
                            <MemoTableRow
                                key={rowKey ? rowKey(row, rowIndex) : rowIndex}
                                row={row}
                                rowIndex={rowIndex}
                                rowKey={rowKey ? rowKey(row, rowIndex) : rowIndex}
                                columns={columns}
                                active={isRowActive?.(row) ?? false}
                                onRowClick={onRowClick}
                            />
                        ))}
                    </Table.Body>
                </Table>
            </div>
            <div className="semanticDataTableFooter">
                <div className="semanticDataTableFooterSummary">
                    {filteredAndSortedData.length === 0
                        ? '0 rows'
                        : `${visibleRowsStart}-${visibleRowsEnd} of ${filteredAndSortedData.length} rows`}
                </div>
                <Pagination
                    size="mini"
                    activePage={activePage}
                    totalPages={totalPages}
                    boundaryRange={1}
                    siblingRange={1}
                    ellipsisItem={totalPages > 5 ? undefined : null}
                    firstItem={totalPages > 1 ? undefined : null}
                    lastItem={totalPages > 1 ? undefined : null}
                    prevItem={totalPages > 1 ? undefined : null}
                    nextItem={totalPages > 1 ? undefined : null}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

const SemanticDataTable = React.forwardRef(SemanticDataTableInner) as <T>(
    props: SemanticDataTableProps<T> & { ref?: React.Ref<SemanticDataTableHandle> },
) => React.ReactElement;

export default SemanticDataTable;
